import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiUsers } from 'react-icons/fi';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

const HealthEquity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareBy, setCompareBy] = useState('race');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await dashboardAPI.getHealthEquity();
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  const { byRace = [], byRegion = [], byGender = [], disparityFlags = [], equityIndex = 0 } = data || {};

  const comparisonData = compareBy === 'race' ? byRace : compareBy === 'region' ? byRegion : byGender;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Health Equity & Disparities</h1>
        <p style={{ color: '#64748b', marginTop: 4 }}>Analyze health outcome disparities across demographics and regions</p>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { icon: FiTrendingUp, label: 'Equity Index', value: equityIndex?.toFixed?.(1) || '—', sub: 'Higher is more equitable', color: '#3b82f6' },
          { icon: FiAlertTriangle, label: 'Disparity Flags', value: disparityFlags.length, sub: 'Gaps detected', color: '#ef4444' },
          { icon: FiUsers, label: 'Demographics Tracked', value: byRace.length + byGender.length, color: '#8b5cf6' },
          { icon: FiCheckCircle, label: 'Regions Analyzed', value: byRegion.length, color: '#10b981' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{s.label}</p>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{s.value}</p>
                {s.sub && <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{s.sub}</p>}
              </div>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon size={22} color={s.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Disparity Flags */}
      {disparityFlags.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#991b1b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle size={20} /> AI-Flagged Equity Gaps
          </h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {disparityFlags.map((flag, i) => (
              <div key={i} style={{ padding: '14px 18px', background: '#fff', borderRadius: 10, borderLeft: '4px solid #ef4444', fontSize: 14, color: '#334155' }}>
                <strong>{flag.group}</strong> — {flag.metric}: <span style={{ color: '#ef4444', fontWeight: 600 }}>{flag.value?.toFixed?.(1) || flag.value}</span>
                {flag.comparison && <span style={{ color: '#64748b' }}> vs avg {flag.comparison?.toFixed?.(1)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparison Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['race', 'region', 'gender'].map(opt => (
          <button key={opt} onClick={() => setCompareBy(opt)}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer', background: compareBy === opt ? '#3b82f6' : '#f1f5f9', color: compareBy === opt ? '#fff' : '#64748b', transition: 'all 0.2s' }}>
            By {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Disease Cases by {compareBy.charAt(0).toUpperCase() + compareBy.slice(1)}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Cases" />
              {comparisonData[0]?.avgSeverity !== undefined && <Bar dataKey="avgSeverity" fill="#ef4444" radius={[4, 4, 0, 0]} name="Avg Severity" />}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Outcome Distribution</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={comparisonData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="_id" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="resolved" fill="#10b981" radius={[0, 4, 4, 0]} name="Resolved" stackId="a" />
              <Bar dataKey="active" fill="#ef4444" radius={[0, 4, 4, 0]} name="Active" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Side-by-Side Comparison Table */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Health Outcomes Comparison</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Group', 'Total Cases', 'Active', 'Resolved', 'Critical %', 'Avg Severity'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>{item._id}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.count || 0}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#ef4444' }}>{item.active || 0}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#10b981' }}>{item.resolved || 0}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.criticalPct?.toFixed?.(1) || '—'}%</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: item.avgSeverity > 2 ? '#fef2f2' : item.avgSeverity > 1 ? '#fefce8' : '#f0fdf4',
                      color: item.avgSeverity > 2 ? '#991b1b' : item.avgSeverity > 1 ? '#854d0e' : '#166534' }}>
                      {item.avgSeverity?.toFixed?.(2) || '—'}
                    </span>
                  </td>
                </tr>
              ))}
              {comparisonData.length === 0 && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No data available</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Title VI Compliance */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Title VI Compliance Tracking</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {[
            { label: 'Language Access Services', status: 'compliant', desc: 'Translation available in 12 languages' },
            { label: 'Non-Discrimination Policies', status: 'compliant', desc: 'Policies reviewed and updated' },
            { label: 'Community Engagement', status: 'review', desc: 'Engagement plan under review' },
            { label: 'Data Disaggregation', status: 'compliant', desc: 'Data collected by race, ethnicity, language' },
            { label: 'Grievance Procedures', status: 'compliant', desc: 'Process established and documented' },
            { label: 'Staff Training', status: 'action', desc: 'Annual training scheduled for Q2' },
          ].map((item, i) => (
            <div key={i} style={{ padding: '14px 18px', borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.desc}</div>
              </div>
              <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: item.status === 'compliant' ? '#dcfce7' : item.status === 'review' ? '#fef3c7' : '#fee2e2',
                color: item.status === 'compliant' ? '#166534' : item.status === 'review' ? '#854d0e' : '#991b1b' }}>
                {item.status === 'compliant' ? '✓ Compliant' : item.status === 'review' ? '⟳ Review' : '! Action'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HealthEquity;
